import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/utils.js';

export type DialogIconVariant = 'danger' | 'warning' | 'info';

export interface DialogBackdropProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function DialogBackdrop({ className, children, ...props }: DialogBackdropProps) {
  return (
    <div className={cn('dialog-backdrop', className)} role="presentation" {...props}>
      {children}
    </div>
  );
}

export interface DialogProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function Dialog({ className, children, role = 'dialog', ...props }: DialogProps) {
  return (
    <div className={cn('dialog', className)} role={role} aria-modal="true" {...props}>
      {children}
    </div>
  );
}

export interface DialogHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function DialogHeader({ className, children, ...props }: DialogHeaderProps) {
  return (
    <div className={cn('dialog-header', className)} {...props}>
      {children}
    </div>
  );
}

export interface DialogHeaderMainProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function DialogHeaderMain({ className, children, ...props }: DialogHeaderMainProps) {
  return (
    <div className={cn('dialog-header-main', className)} {...props}>
      {children}
    </div>
  );
}

export interface DialogTitleProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function DialogTitle({ className, children, ...props }: DialogTitleProps) {
  return (
    <div className={cn('dialog-title', className)} {...props}>
      {children}
    </div>
  );
}

export interface DialogIconProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: DialogIconVariant;
  children: ReactNode;
}

const iconVariantClassName: Record<DialogIconVariant, string> = {
  danger: 'dialog-icon--danger',
  warning: 'dialog-icon--warning',
  info: 'dialog-icon--info',
};

export function DialogIcon({ variant = 'danger', className, children, ...props }: DialogIconProps) {
  return (
    <span className={cn('dialog-icon', iconVariantClassName[variant], className)} {...props}>
      {children}
    </span>
  );
}

export interface DialogBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function DialogBody({ className, children, ...props }: DialogBodyProps) {
  return (
    <div className={cn('dialog-body', className)} {...props}>
      {children}
    </div>
  );
}

export interface DialogFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function DialogFooter({ className, children, ...props }: DialogFooterProps) {
  return (
    <div className={cn('dialog-footer', className)} {...props}>
      {children}
    </div>
  );
}
